import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Bug, MessageCircle, HelpCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/services/api";

type FeedbackType = "technical_defect" | "general_feedback" | "other";

const FEEDBACK_OPTIONS: {
  id: FeedbackType;
  label: string;
  description: string;
  icon: typeof Bug;
}[] = [
  {
    id: "technical_defect",
    label: "Technical Defect",
    description: "Something is broken or not working as expected",
    icon: Bug,
  },
  {
    id: "general_feedback",
    label: "General Feedback",
    description: "Suggestions, ideas, or general comments",
    icon: MessageCircle,
  },
  {
    id: "other",
    label: "Other",
    description: "Anything else you'd like to share",
    icon: HelpCircle,
  },
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, []);

  const resetAndClose = useCallback(() => {
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    setSelectedType(null);
    setMessage("");
    setError(null);
    setSubmitted(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedType || !message.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await api.submitFeedback({
        type: selectedType,
        message: message.trim(),
        page_url: window.location.pathname,
      });
      setSubmitted(true);
      autoCloseTimer.current = setTimeout(resetAndClose, 1500);
    } catch {
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Send Feedback</h2>
            <button
              onClick={resetAndClose}
              className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-white/70 mt-1">
            Help us improve by sharing your thoughts
          </p>
        </div>

        {submitted ? (
          <div className="p-8 flex flex-col items-center gap-3">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-foreground font-medium">Thank you for your feedback!</p>
          </div>
        ) : (
          <div className="p-5">
            {/* Type selector */}
            <p className="text-sm font-medium text-foreground mb-3">
              What type of feedback is this?
            </p>
            <div className="space-y-2 mb-5">
              {FEEDBACK_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <label
                    key={option.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedType === option.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="feedback-type"
                      value={option.id}
                      checked={selectedType === option.id}
                      onChange={() => setSelectedType(option.id)}
                      className="sr-only"
                    />
                    <div
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        selectedType === option.id
                          ? "border-primary"
                          : "border-muted-foreground/40"
                      }`}
                    >
                      {selectedType === option.id && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-foreground text-sm">
                          {option.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {option.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Message */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Your message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us more..."
              rows={4}
              maxLength={5000}
              className="w-full px-3 py-2 border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground rounded-lg shadow-sm resize-none transition-colors focus:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <div className="text-xs text-muted-foreground text-right mt-1">
              {message.length}/5000
            </div>

            {error && (
              <p className="text-sm text-destructive mt-2">{error}</p>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button
                variant="brand"
                className="flex-1"
                disabled={!selectedType || !message.trim() || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
